import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import styles from './Button.module.css'

type ButtonVariant = 'primary' | 'secondary' | 'ghost'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps {
  children: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  /** Internal route — rendered as a react-router <Link>. */
  to?: string
  /** External URL, mailto:, or in-page anchor — rendered as an <a>. */
  href?: string
  /** Force new-tab semantics for external links. */
  external?: boolean
  className?: string
  ariaLabel?: string
}

/**
 * Shared call-to-action element. Renders a router <Link> when `to` is given,
 * otherwise a plain <a>. Styling comes entirely from design tokens.
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  to,
  href,
  external = false,
  className,
  ariaLabel,
}: ButtonProps) {
  const classNames = [styles.button, styles[variant], styles[size], className]
    .filter(Boolean)
    .join(' ')

  if (to) {
    return (
      <Link to={to} className={classNames} aria-label={ariaLabel}>
        {children}
      </Link>
    )
  }

  const rel = external ? 'noopener noreferrer' : undefined
  const target = external ? '_blank' : undefined

  return (
    <a href={href} className={classNames} aria-label={ariaLabel} rel={rel} target={target}>
      {children}
    </a>
  )
}
